import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import PhoneInput from "@/components/PhoneInput";

export default function NewCustomerDialog({ trigger, defaultStatus = "lead", onCreated }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [org, setOrg] = useState("");
  const [looking, setLooking] = useState(false);
  const [deps, setDeps] = useState([]);
  const [locs, setLocs] = useState([]);
  const [form, setForm] = useState({ legal_name: "", org_number: "", company_form: "", industry: "", address: "", email: "", phone: "", location: "", departments: [] });
  const [saving, setSaving] = useState(false);
  const [dupe, setDupe] = useState(null);
  const [existingMatch, setExistingMatch] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/departments").then((r) => setDeps(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocs(r.data)).catch(() => {});
  }, [open]);

  const toggleDept = (d) => setForm((f) => ({ ...f, departments: f.departments.includes(d) ? f.departments.filter((x) => x !== d) : [...f.departments, d] }));

  const checkExisting = async (orgRaw) => {
    const norm = (orgRaw || "").replace(/\D/g, "");
    if (norm.length < 9) { setExistingMatch(null); return; }
    try {
      const { data } = await api.get(`/customers?q=${norm}`);
      const hit = (data || []).find((c) => (c.org_number || "").replace(/\D/g, "") === norm && !c.archived);
      setExistingMatch(hit || null);
    } catch { setExistingMatch(null); }
  };

  const lookup = async () => {
    if (!org) return;
    setLooking(true);
    try {
      const clean = org.replace(/\s/g, "");
      const { data } = await api.get(`/brreg/${clean}`);
      setForm((f) => ({
        ...f, legal_name: data.legal_name || "", org_number: data.org_number || org,
        company_form: data.company_form || "", industry: data.industry || "",
        address: data.address || "", website: data.website || "",
        industry_code: data.industry_code, founded_date: data.founded_date,
        vat_registered: data.vat_registered,
      }));
      toast.success("Selskapsdata hentet fra Enhetsregisteret");
      checkExisting(clean);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setLooking(false); }
  };

  const save = async () => {
    if (!form.legal_name) { toast.error("Navn er påkrevd"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/customers", { ...form, status: defaultStatus, pipeline_stage: defaultStatus === "lead" ? "nytt_lead" : "onboarding" });
      toast.success("Opprettet");
      setOpen(false);
      setForm({ legal_name: "", org_number: "", company_form: "", industry: "", address: "", email: "", phone: "", location: "", departments: [] });
      setOrg(""); setExistingMatch(null);
      onCreated?.(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d && typeof d === "object" && d.code === "duplicate_org_number") {
        setDupe(d);
      } else {
        toast.error(apiError(e));
      }
    }
    finally { setSaving(false); }
  };

  const openExisting = (id) => {
    setDupe(null); setOpen(false); nav(`/kunder/${id}`);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-popover border-border max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-xl">Nytt {defaultStatus === "lead" ? "lead" : "kunde"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="label-caps">Organisasjonsnummer</Label>
            <div className="flex gap-2 mt-1.5">
              <Input data-testid="new-org-input" value={org} onChange={(e) => setOrg(e.target.value)}
                onBlur={() => checkExisting(org)}
                placeholder="9 siffer" className="bg-secondary border-border" onKeyDown={(e) => e.key === "Enter" && lookup()} />
              <Button data-testid="brreg-lookup-btn" variant="secondary" onClick={lookup} disabled={looking}>
                {looking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Hent
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Henter data fra Brønnøysundregistrene</p>
            {existingMatch && (
              <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-300" data-testid="dup-warning-banner">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  Det finnes allerede et kundekort for dette organisasjonsnummeret: <strong>{existingMatch.legal_name}</strong>.
                  <button type="button" onClick={() => openExisting(existingMatch.id)} className="ml-1 underline hover:text-amber-200" data-testid="dup-warning-open">Åpne kundekort</button>
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="label-caps">Juridisk navn *</Label>
              <Input data-testid="new-name-input" value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} className="mt-1.5 bg-secondary border-border" />
            </div>
            <div><Label className="label-caps">Selskapsform</Label><Input value={form.company_form} onChange={(e) => setForm({ ...form, company_form: e.target.value })} className="mt-1.5 bg-secondary border-border" /></div>
            <div><Label className="label-caps">Bransje</Label><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="mt-1.5 bg-secondary border-border" /></div>
            <div className="col-span-2"><Label className="label-caps">Adresse</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5 bg-secondary border-border" /></div>
            <div><Label className="label-caps">E-post</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-secondary border-border" /></div>
            <div><Label className="label-caps">Telefon</Label><div className="mt-1.5"><PhoneInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="new-customer-phone" /></div></div>
            <div className="col-span-2">
              <Label className="label-caps">Lokasjon</Label>
              <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                <SelectTrigger data-testid="new-location-select" className="mt-1.5 bg-secondary border-border"><SelectValue placeholder="Velg lokasjon" /></SelectTrigger>
                <SelectContent className="bg-popover border-border">{locs.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="label-caps">Avdeling(er)</Label>
              <div className="flex flex-wrap gap-3 mt-2">
                {deps.map((d) => (
                  <label key={d} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.departments.includes(d)} onCheckedChange={() => toggleDept(d)} data-testid={`new-dept-${d}`} /> {d}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="new-customer-save" onClick={save} disabled={saving || !!existingMatch} className="bg-white text-black hover:bg-white/90">
            {saving ? "Lagrer…" : "Opprett"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AlertDialog open={!!dupe} onOpenChange={(v) => !v && setDupe(null)}>
      <AlertDialogContent className="bg-popover border-border" data-testid="dup-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-400" /> Kunden finnes allerede</AlertDialogTitle>
          <AlertDialogDescription>{dupe?.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="dup-dialog-cancel">Lukk</AlertDialogCancel>
          <AlertDialogAction data-testid="dup-dialog-open" onClick={() => openExisting(dupe?.customer_id)}>Åpne eksisterende kundekort</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
